import { ChartNoAxesColumn, ShieldCheck, Tags } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Navigate } from "react-router";
import { Logo } from "@/components/Logo";
import { Button, ErrorNote, Field, Input } from "@/components/ui";
import { useSession } from "@/lib/auth";

const POINTS = [
  {
    icon: Tags,
    title: "Every entry categorized",
    text: "Keyword rules per organization, corrections feed back in.",
  },
  {
    icon: ChartNoAxesColumn,
    title: "Affordability at a glance",
    text: "Income, essential spend and debt service.",
  },
  {
    icon: ShieldCheck,
    title: "Isolated per organization",
    text: "Row-level security on every tenant's data.",
  },
];

export function LoginPage() {
  const { signIn, signedIn } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (signedIn) return <Navigate to="/" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-full lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <Logo size="lg" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Sign in</h1>
          <p className="mt-1 text-sm text-slate-500">Bank statement analysis for your organization.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <Field label="E-mail">
              <Input
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <ErrorNote error={error} />
            <Button type="submit" className="w-full" loading={busy}>
              Sign in
            </Button>
          </form>
          {import.meta.env.DEV && (
            <p className="mt-6 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">
              Demo: <code className="font-medium">admin@codego.local</code> /{" "}
              <code className="font-medium">demo-password-1</code> — other roles in the README.
            </p>
          )}
        </div>
      </div>
      <div className="relative hidden overflow-hidden bg-brand-900 lg:flex lg:items-center lg:justify-center">
        <div className="absolute -top-24 -right-24 size-96 rounded-full bg-brand-700/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 size-96 rounded-full bg-brand-500/20 blur-3xl" />
        <ul className="relative max-w-md space-y-8 px-10">
          {POINTS.map((p) => (
            <li key={p.title} className="flex gap-4">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15">
                <p.icon className="size-5 text-brand-100" />
              </span>
              <div>
                <div className="font-medium text-white">{p.title}</div>
                <div className="mt-0.5 text-sm text-brand-100/80">{p.text}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
